import { useState, MouseEvent, useRef } from 'react'
import { ModalDialog, ModalDialogBody, ModalDialogContent, Button, Typography, Icon, ModalDialogFooter } from '@vp/swan'
import { useSelector } from 'react-redux'
import equal from 'fast-deep-equal/es6'

import { Filters } from '~/app/features/Gallery/components/Filters'
import { FilterMenuHeader } from '~/app/features/Gallery/components/Header/ActionHeader/components/FilterMenu/FilterMenuHeader'
import { FilterMenuFooter } from '~/app/features/Gallery/components/Header/ActionHeader/components/FilterMenu/FilterMenuFooter'
import { useTranslations } from '~/app/features/Gallery/hooks/useTranslations'
import { findAllRemovableRefinements } from '~/app/features/Gallery/components/Subheader/RefinementsList/findAllRemovableRefinements'
import classnames from 'classnames'
import { useShowFavorites } from '~/app/features/Gallery/hooks/useShowFavorites'
import { RefinementsList } from '~/app/features/Gallery/components/Subheader/RefinementsList'
import { totalEntityCountSelector } from '~/app/store/paging'
import { scrollUpToElement } from '~/app/utils/scrollToElement'
import { galleryHeaderId } from '~/app/features/Gallery/components/Header/ActionHeader/constants'
import { filterMenuDialogId } from '~/app/features/Gallery/components/Header/ActionHeader/components/FilterMenu/constants'
import { refinementsSelector } from '~/app/store/refinement'
import { ANALYTICS_EVENT_ACTIONS } from '~/shared/constants'
import { useAnalytics } from '~/app/features/Gallery/hooks/useAnalytics'
import { SearchButton } from '~/app/features/Gallery/components/Header/ActionHeader/components/FilterMenu/components/SearchButton'

interface FilterMenuProps {
  shouldRenderSmallMobileIcon?: boolean
  showColorFilterFirst?: boolean
}

export const FilterMenu = ({ shouldRenderSmallMobileIcon, showColorFilterFirst }: FilterMenuProps) => {
  const localize = useTranslations()
  const analytics = useAnalytics()
  const allRefinements = useSelector(refinementsSelector)
  const refinements = useSelector(findAllRemovableRefinements)
  const resultCount = useSelector(totalEntityCountSelector)
  const showFavorites = useShowFavorites()
  const initialAllRefiniments = useRef(allRefinements)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSearchAutoFocus, setIsSearchAutoFocus] = useState(false)

  const refinementsCount = Object.values(refinements).length
  const resultCopy = `${resultCount} ${resultCount === 1 ? localize('Result') : localize('Results')}`

  // Open filters modal if photo personalization opened after page refresh or redirect
  // Only for photo personalization

  const handleClose = (): void => {
    setIsModalOpen(false)
    setIsSearchAutoFocus(false)
    if (!equal(initialAllRefiniments.current, allRefinements)) {
      scrollUpToElement(galleryHeaderId)
    }
  }

  const showFilters = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault()

    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_VIEWED,
      eventLabel: 'Filter Button',
      eventDetail: 'Filters Fly-out Open',
      ...analytics.getPageProperties(),
    })

    setIsModalOpen(true)
  }

  const handleSearchButtonClick = (): void => {
    setIsModalOpen(true)
    setIsSearchAutoFocus(true)
  }

  const shouldShowRefinementsCount = refinementsCount > 0

  return (
    <>
      <SearchButton shouldHideText={shouldRenderSmallMobileIcon} onClick={handleSearchButtonClick} />
      <Button
        aria-label={localize('FilterButtonA11yText')}
        aria-labelledby="filter-menu-button-label"
        buttonShape="round"
        className={classnames(
          {
            'favorites-filter-menu-button': showFavorites,
            'mobile-rounded-icon-button': !shouldShowRefinementsCount,
            'filter-menu-button-with-count': shouldShowRefinementsCount && !shouldRenderSmallMobileIcon,
            'filter-menu-button-with-count-small': shouldShowRefinementsCount && shouldRenderSmallMobileIcon,
          },
          'filter-menu-button',
        )}
        iconPosition="left"
        onClick={showFilters}
      >
        <Icon className="filter-icon" iconType="filter" marginLeft={0} marginRight={shouldRenderSmallMobileIcon ? 0 : 2} />
        <span className={classnames({ 'filter-menu-text': showFavorites })} id="filter-menu-button-label">
          {localize('FilterMenuButtonText')}
        </span>
        {shouldShowRefinementsCount && ` (${refinementsCount})`}
      </Button>
      <ModalDialog
        takeOver
        className="filter-menu-dialog stylized-dialog-panel-left stylized-dialog-panel-menu"
        id={filterMenuDialogId}
        isOpen={isModalOpen}
        variant="panel-left"
        onRequestDismiss={handleClose}
      >
        <ModalDialogContent fullBleed aria-label="filter-menu">
          <ModalDialogBody>
            <FilterMenuHeader autoFocus={isSearchAutoFocus} onClose={handleClose} />
            <div className="search-refinements">
              {resultCount > 0 && (
                <Typography className="result-count" fontSize="xsmall">
                  {resultCopy}
                </Typography>
              )}
              <RefinementsList />
            </div>
            <Filters showColorFirst={showColorFilterFirst} />
          </ModalDialogBody>
          <ModalDialogFooter pinned p={0}>
            <FilterMenuFooter onClose={handleClose} />
          </ModalDialogFooter>
        </ModalDialogContent>
      </ModalDialog>
    </>
  )
}

FilterMenu.displayName = 'FilterMenu'
